import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import type { ToolCall } from '../types';
import { runFrontendAction } from '../config/actions';

interface Props {
  messageId: number;
  toolCall: ToolCall;
  approveToolCall: (messageId: number, toolUseId: string) => Promise<void>;
  rejectToolCall: (messageId: number, toolUseId: string) => Promise<void>;
  decideToolCall: (messageId: number, toolUseId: string, decision: 'approved' | 'rejected') => Promise<void>;
}

function describeToolCall(toolCall: ToolCall): string {
  if (toolCall.name === 'recommend_action') {
    const label = (toolCall.input as { label?: string } | undefined)?.label;
    if (label) return label;
  }
  return toolCall.name.replace(/_/g, ' ');
}

/**
 * Inline approval card (`AI-Assistant-Feature.md` §7 Phase 8) — renders the
 * same way regardless of lane, per §6.2's "the card doesn't need to know
 * which lane it's in" (mostly true: it does still need to route Approve to
 * the right pair of calls, per §6).
 *
 * Backend-executed lane (`executionTarget: 'backend'`): Approve/Reject call
 * the real approve/reject endpoints, which resume (or deny) the paused job.
 *
 * Frontend-executed lane (`executionTarget: 'frontend'`, i.e.
 * `recommend_action`): Approve runs the app's own existing action
 * (`config/actions.ts`'s dispatch table) directly, then calls `/decision`
 * purely to record the outcome — nothing on the backend was ever paused.
 */
export function AIChatApprovalCard({ messageId, toolCall, approveToolCall, rejectToolCall, decideToolCall }: Props) {
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const navigate = useNavigate();

  if (toolCall.decision) {
    return (
      <Box sx={{ px: 0.5, py: 0.25 }}>
        <Typography variant="caption" color="text.secondary">
          {describeToolCall(toolCall)} — {toolCall.decision}
        </Typography>
      </Box>
    );
  }

  const handleApprove = async () => {
    setBusy(true);
    setActionError(null);
    try {
      if (toolCall.executionTarget === 'frontend') {
        const input = toolCall.input as { actionId?: string; params?: Record<string, unknown> } | undefined;
        if (input?.actionId) {
          await runFrontendAction(input.actionId, input.params, { navigate });
        }
        await decideToolCall(messageId, toolCall.toolUseId, 'approved');
      } else {
        await approveToolCall(messageId, toolCall.toolUseId);
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to run the action');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    setBusy(true);
    setActionError(null);
    try {
      if (toolCall.executionTarget === 'frontend') {
        await decideToolCall(messageId, toolCall.toolUseId, 'rejected');
      } else {
        await rejectToolCall(messageId, toolCall.toolUseId);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        px: 1.25,
        py: 1,
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="body2" sx={{ mb: 0.75 }}>
        {describeToolCall(toolCall)}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="contained" disabled={busy} onClick={() => void handleApprove()}>
          {busy ? <CircularProgress size={14} color="inherit" /> : 'Approve'}
        </Button>
        <Button size="small" variant="outlined" disabled={busy} onClick={() => void handleReject()}>
          Reject
        </Button>
      </Stack>
      {actionError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {actionError}
        </Typography>
      )}
    </Box>
  );
}
